import React, { Suspense, useEffect, useId, useRef } from 'react'
import { PanelRightClose, PanelRightOpen } from 'lucide-react'

export interface TabSubItem {
    id: string
    label: string
    badge?: React.ReactNode
    content: React.ReactNode | (() => React.ReactNode)
}

export interface TabItem {
    id: string
    label: string
    icon: React.ReactNode
    badge?: React.ReactNode
    indicatorTone?: 'info' | 'success' | 'caution' | 'warning' | 'danger' | 'neutral'
    subTabs?: TabSubItem[]
    content?: React.ReactNode | (() => React.ReactNode)
}

interface TabbedOverlayPanelProps {
    tabs: TabItem[]
    activeTab: string
    onTabChange: (tabId: string) => void
    activeSubTab?: Record<string, string>
    onSubTabChange?: (tabId: string, subTabId: string) => void
    isOpen: boolean
    onToggleOpen: (open?: boolean) => void
    isDark?: boolean
    className?: string
    presentation?: 'floating' | 'dashboard'
    focusRequest?: number
}

export default function TabbedOverlayPanel({
    tabs,
    activeTab,
    onTabChange,
    activeSubTab = {},
    onSubTabChange,
    isOpen,
    onToggleOpen,
    isDark = true,
    className = '',
    presentation = 'floating',
    focusRequest = 0,
}: TabbedOverlayPanelProps) {
    const panelId = useId()
    const panelRef = useRef<HTMLElement>(null)
    useEffect(() => {
        if (presentation !== 'dashboard' || !focusRequest) return
        panelRef.current?.focus({ preventScroll: true })
        if (window.matchMedia('(max-width: 1279px)').matches) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [focusRequest, presentation])
    const currentTab = tabs.find((t) => t.id === activeTab) || tabs[0]

    const currentSubTabId = currentTab?.subTabs
        ? activeSubTab[currentTab.id] || currentTab.subTabs[0]?.id
        : undefined

    const currentSubTab =
        currentTab?.subTabs?.find((st) => st.id === currentSubTabId) ||
        currentTab?.subTabs?.[0]
    const content = currentTab?.subTabs ? currentSubTab?.content : currentTab?.content

    if (presentation === 'dashboard') return <aside className="viewer-inspector" aria-label="Contrôles du chargement">
        <div className="viewer-inspector-heading"><div><p className="text-sm font-semibold">Contrôler le plan</p><p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Inspectez les articles et les points de vigilance.</p></div></div>
        <div className="viewer-inspector-tabs" role="tablist" aria-label="Analyses du chargement">
            {tabs.map((tab, index) => <button key={tab.id} id={`${panelId}-${tab.id}`} role="tab" aria-selected={tab.id === activeTab} aria-controls={`${panelId}-panel`} tabIndex={tab.id === activeTab ? 0 : -1}
                className="viewer-inspector-tab" title={tab.label} onClick={() => onTabChange(tab.id)} onKeyDown={event => {
                    let next = index
                    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length
                    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
                    else if (event.key === 'Home') next = 0
                    else if (event.key === 'End') next = tabs.length - 1
                    else return
                    event.preventDefault(); onTabChange(tabs[next].id)
                    document.getElementById(`${panelId}-${tabs[next].id}`)?.focus()
                }}><span className="relative" aria-hidden="true">{tab.icon}{tab.indicatorTone && tab.indicatorTone !== 'neutral' && <span className={`viewer-tab-dot viewer-tab-dot-${tab.indicatorTone}`} />}</span><span>{({ cg: 'Centre de gravité', score_metrics: 'Performance', pressure: 'Sol' } as Record<string, string>)[tab.id] || tab.label}</span></button>)}
        </div>
        <section ref={panelRef} tabIndex={0} role="tabpanel" id={`${panelId}-panel`} aria-labelledby={`${panelId}-${activeTab}`} className="viewer-inspector-body">
            {currentTab?.subTabs && <div className="viewer-inspector-subtabs" role="group" aria-label={`Vues ${currentTab.label}`}>
                {currentTab.subTabs.map(tab => <button key={tab.id} aria-pressed={tab.id === currentSubTabId} className="viewer-inspector-subtab" onClick={() => onSubTabChange?.(currentTab.id, tab.id)}>{tab.label}{tab.badge}</button>)}
            </div>}
            <div className="min-w-0 p-3 sm:p-4"><Suspense fallback={<div role="status" className="p-4 text-sm text-slate-500">Chargement de l’analyse…</div>}>{typeof content === 'function' ? content() : content}</Suspense></div>
        </section>
    </aside>

    // Render collapsed state: floating vertical dock of tab icons
    if (!isOpen) {
        return (
            <div className="animate-tab-panel absolute top-[240px] right-4 z-[50] flex flex-col gap-2 pointer-events-auto lg:top-[204px] 2xl:top-[148px]">
                <div
                    className="flex flex-col gap-1.5 p-1.5 rounded-2xl shadow-2xl transition-all duration-300"
                    style={{
                        background: isDark
                            ? 'rgba(8,12,22,0.92)'
                            : 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'
                            }`,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => onToggleOpen(true)}
                        className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors focus:outline-none ${isDark
                                ? 'text-slate-400 hover:text-white hover:bg-white/[0.08]'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                        title="Agrandir le panneau"
                        aria-label="Agrandir le panneau"
                    >
                        <PanelRightOpen className="h-4 w-4" aria-hidden="true" />
                    </button>
                    {tabs.map((tab) => {
                        const isActive = tab.id === activeTab
                        const indicatorTone = tab.indicatorTone || (tab.badge ? 'info' : null)
                        const indicatorColor = indicatorTone === 'success' ? 'bg-emerald-500'
                            : indicatorTone === 'caution' ? 'bg-amber-500'
                                : indicatorTone === 'warning' ? 'bg-orange-500'
                                : indicatorTone === 'danger' ? 'bg-red-500'
                                    : indicatorTone === 'neutral' ? 'bg-slate-400'
                                        : 'bg-blue-500'
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => {
                                    onTabChange(tab.id)
                                    onToggleOpen(true)
                                }}
                                className={`group relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 focus:outline-none ${isActive
                                        ? 'bg-gradient-to-tr from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                                        : isDark
                                            ? 'text-slate-400 hover:text-white hover:bg-white/[0.08]'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                    }`}
                                title={tab.label}
                            >
                                {tab.icon}
                                {/* Badge indicator on collapsed icon */}
                                {indicatorTone && (
                                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                        {(indicatorTone === 'warning' || indicatorTone === 'danger') && (
                                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${indicatorColor} opacity-75`} />
                                        )}
                                        <span className={`relative inline-flex rounded-full h-3 w-3 ${indicatorColor}`} />
                                    </span>
                                )}
                                {/* Hover Tooltip */}
                                <div
                                    className="absolute right-12 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
                                    style={{
                                        background: isDark
                                            ? 'rgba(15,23,42,0.95)'
                                            : 'rgba(255,255,255,0.98)',
                                        color: isDark ? '#fff' : '#0f172a',
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)'
                                            }`,
                                    }}
                                >
                                    {tab.label}
                                </div>
                            </button>
                        )
                    })}
                </div>
            </div>
        )
    }

    return (
        <div
            className={`animate-tab-panel absolute top-[240px] right-4 z-[50] flex pointer-events-auto rounded-2xl shadow-2xl transition-all duration-500 overflow-hidden max-h-[calc(100vh-256px)] lg:top-[204px] lg:max-h-[calc(100vh-220px)] 2xl:top-[148px] 2xl:max-h-[calc(100vh-164px)] ${className}`}
            style={{
                width: 'min(560px, calc(100vw - 2rem))',
                background: isDark
                    ? 'rgba(8,12,22,0.94)'
                    : 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'
                    }`,
            }}
        >
            {/* ── TAB CONTENT BODY (LEFT) ───────────────────────────────────────── */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* ── SUB-TABS (if any) ──────────────────────────────────────────── */}
                {currentTab?.subTabs && currentTab.subTabs.length > 0 && (
                    <div
                        className={`px-3 py-2 border-b ${isDark
                                ? 'border-white/[0.06] bg-black/10'
                                : 'border-slate-200 bg-slate-50/80'
                            }`}
                    >
                        <div
                            className={`flex items-center gap-1 p-1 rounded-xl border ${isDark
                                    ? 'bg-white/[0.04] border-white/[0.06]'
                                    : 'bg-slate-200/60 border-slate-300/60'
                                }`}
                        >
                            {currentTab.subTabs.map((subTab) => {
                                const isSubActive = subTab.id === currentSubTabId
                                return (
                                    <button
                                        key={subTab.id}
                                        type="button"
                                        onClick={() => onSubTabChange?.(currentTab.id, subTab.id)}
                                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all duration-200 focus:outline-none min-w-0 ${isSubActive
                                                ? isDark
                                                    ? 'bg-white/10 text-white font-bold shadow-sm border border-white/10'
                                                    : 'bg-white text-blue-700 font-bold shadow-sm border border-slate-300'
                                                : isDark
                                                    ? 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-300/40'
                                            }`}
                                        title={subTab.label}
                                    >
                                        <span className="truncate">{subTab.label}</span>
                                        {subTab.badge}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* ── CONTENT ───────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                    <div key={`${currentTab?.id ?? ''}:${currentSubTabId ?? ''}`} className="animate-slide-in-from-right">
                        {typeof content === 'function' ? content() : content}
                    </div>
                </div>
            </div>

            {/* ── VERTICAL TABS (RIGHT) ───────────────────────────────────────── */}
            <div
                className={`flex flex-col border-l ${isDark
                        ? 'bg-black/30 border-white/[0.08]'
                        : 'bg-slate-100/90 border-slate-200'
                    }`}
            >
                {/* Panel toggle at top */}
                <div className="p-2 border-b border-white/[0.08]">
                    <button
                        type="button"
                        onClick={() => onToggleOpen(false)}
                        className={`p-1.5 rounded-lg transition-colors ${isDark
                                ? 'text-slate-400 hover:text-white hover:bg-white/[0.08]'
                                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                            }`}
                        title="Réduire le panneau"
                        aria-label="Réduire le panneau"
                    >
                        <PanelRightClose className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>

                {/* Vertical tabs */}
                <div className="flex flex-col gap-1 p-2">
                    {tabs.map((tab) => {
                        const isActive = tab.id === activeTab
                        const indicatorTone = tab.indicatorTone || (tab.badge ? 'info' : null)
                        const indicatorColor = indicatorTone === 'success' ? 'bg-emerald-500'
                            : indicatorTone === 'caution' ? 'bg-amber-500'
                                : indicatorTone === 'warning' ? 'bg-orange-500'
                                    : indicatorTone === 'danger' ? 'bg-red-500'
                                        : indicatorTone === 'neutral' ? 'bg-slate-400'
                                            : 'bg-blue-500'
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => onTabChange(tab.id)}
                                className={`flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-all duration-200 focus:outline-none ${isActive
                                        ? 'bg-gradient-to-b from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25'
                                        : isDark
                                            ? 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/80'
                                    }`}
                                title={tab.label}
                            >
                                <span className="relative h-5 w-5 flex-shrink-0">
                                    {tab.icon}
                                    {indicatorTone && (
                                        <span className="absolute -top-1 -right-1 flex h-3 w-3" aria-hidden="true">
                                            {(indicatorTone === 'warning' || indicatorTone === 'danger') && (
                                                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${indicatorColor} opacity-75`} />
                                            )}
                                            <span className={`relative inline-flex rounded-full h-3 w-3 ${indicatorColor}`} />
                                        </span>
                                    )}
                                </span>
                                <span className="whitespace-nowrap text-[10px] font-semibold leading-tight">{tab.label}</span>
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
